import React, { useState, useContext } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Modal, Form, Button, Header, Icon,
} from 'semantic-ui-react';
import MessageContext from '../../contexts/MessageContext';
import { MANAGED_PROJECTS, UPCOMING_MEETINGS } from '../../queries/profile';
import { CANCEL_JOB } from '../../queries/meeting';
import { JOB_MEETING_CONTEXT } from '../../queries/job';
import BlockTimeOption from './BlockTimeOption';

const CANCELLATION_REASONS = [
  'Scheduling conflict',
  'Scope change',
  'Not the right fit',
  'Expert no longer needed',
  'Other',
];

const CancelJob = ({ job, children }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [blockTime, setBlockTime] = useState(false);
  const { setSuccessPopup, setErrorPopup } = useContext(MessageContext);

  const {
    id, project, expert, meetingAt,
  } = job;
  const isInterview = project.type === 'Interview';
  const itemName = isInterview ? 'meeting' : 'survey';
  const hasUpcomingMeeting = isInterview && meetingAt && new Date(meetingAt) > new Date();

  // Interviewers are not loaded by PROJECT_JOBS to keep the list-view payload
  // small. Fetch them on demand when this modal opens; Apollo merges the
  // result into the same Job:id entity (and reuses the cache if MeetingModal
  // already fetched them).
  const { data } = useQuery(JOB_MEETING_CONTEXT, {
    skip: !open,
    variables: { id },
  });
  const interviewers = data?.job?.interviewers ?? [];

  const handleOpen = () => {
    setReason('');
    setDetails('');
    setBlockTime(false);
    setOpen(true);
  };

  const [cancelJob, { loading }] = useMutation(CANCEL_JOB, {
    refetchQueries: [
      { query: UPCOMING_MEETINGS },
      { query: MANAGED_PROJECTS },
    ],
    onCompleted: () => {
      setSuccessPopup({
        title: `${project.title} with ${expert.fullName} has been canceled`,
        content: isInterview ? `This event will be removed from your calendar and upcoming meetings.
          Please head over to the Settings > Availability page if you need to update your availability.` : null,
        onClose: () => setOpen(false),
      });
    },
    onError: (err) => {
      setErrorPopup({
        title: `Failed to cancel this ${itemName}`,
        content: err.message,
        onClose: () => setOpen(false),
      });
    },
  });

  const handleCancel = () => {
    const trimmedDetails = details.trim();
    cancelJob({
      variables: {
        jobId: id,
        reason: trimmedDetails ? `${reason}: ${trimmedDetails}` : reason,
        blockTime: hasUpcomingMeeting ? blockTime : false,
      },
    });
  };

  const interviewerNames = interviewers.map((i) => i.fullName || i.email);

  return (
    <Modal
      closeIcon={!loading}
      closeOnEscape={!loading}
      closeOnDimmerClick={!loading}
      open={open}
      onOpen={handleOpen}
      onClose={() => !loading && setOpen(false)}
      trigger={children}
      className="setting-modal-layout change-meeting-modal"
    >
      <Modal.Header>{isInterview ? 'Cancel Meeting' : 'Cancel Survey'}</Modal.Header>
      <Modal.Content>
        <Header>
          {`You are about to cancel ${project.title} with ${expert.fullName}`}
        </Header>
        <Form>
          <div className="reason-label">
            <strong>Reason for cancellation</strong>
            {' (internal only, not shared with the expert)'}
          </div>
          <div className="reason-options">
            {CANCELLATION_REASONS.map((option) => (
              <Button
                key={option}
                type="button"
                className={`reason-option${reason === option ? ' selected' : ''}`}
                disabled={loading}
                onClick={() => setReason(option)}
              >
                {option}
              </Button>
            ))}
          </div>
          <Form.TextArea
            rows={2}
            placeholder="Add details (optional)"
            value={details}
            disabled={loading}
            onChange={(e, { value }) => setDetails(value)}
          />
        </Form>
        {hasUpcomingMeeting && (
          <BlockTimeOption
            meetingAt={meetingAt}
            checked={blockTime}
            onChange={setBlockTime}
            disabled={loading}
            helpText="Leave unchecked to keep this time open for other experts to book."
          />
        )}
        <div className="change-warning">
          <Icon name="warning sign" />
          <div>
            <div>
              <strong>{'This can\'t be undone. '}</strong>
              {`Once canceled, you can't re-invite ${expert.fullName} to this project.`}
            </div>
            {isInterview && interviewerNames.length > 0 && (
              <div>
                {`Interviewer${interviewerNames.length > 1 ? 's' : ''} affected: ${interviewerNames.join(', ')}`}
              </div>
            )}
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions>
        <span className="subtext">
          The expert will receive a cancellation notice by email.
        </span>
        <Button className="basic-btn" disabled={loading} onClick={() => setOpen(false)}>
          {`Keep ${itemName}`}
        </Button>
        <Button
          loading={loading}
          disabled={!reason || loading}
          className="primary-btn"
          onClick={handleCancel}
        >
          {`Cancel ${itemName}`}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default CancelJob;
